import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
 Loader2,
 ChevronLeft,
 ChevronRight,
 FileText,
 Zap,
 Eye,
 Download,
 Search,
 Filter,
 Mail,
 User,
 Briefcase,
 Calendar,
 CheckCircle,
 XCircle,
 Clock,
} from "lucide-react";
import { toast } from "sonner";
import {
 Dialog,
 DialogContent,
 DialogHeader,
 DialogTitle,
 DialogDescription,
} from "../../components/ui/dialog";
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from "../../components/ui/select";
import RecruiterPageShell from "../../components/recruiter/RecruiterPageShell";
import RecruiterStatCard from "../../components/recruiter/RecruiterStatCard";
import RecruiterEmptyState from "../../components/recruiter/RecruiterEmptyState";
import Loader from "../../components/ui/Loader";
import { glassCard, glassCardHover, glassPanel } from "../../utils/glassStyles";

export default function RecruiterApplicationsPage() {
 const navigate = useNavigate();
 const [applications, setApplications] = useState([]);
 const [filteredApps, setFilteredApps] = useState([]);
 const [search, setSearch] = useState("");
 const [statusFilter, setStatusFilter] = useState("all");
 const [currentPage, setCurrentPage] = useState(1);
 const [loading, setLoading] = useState(true);
 const [selectedApp, setSelectedApp] = useState(null);
 const [showModal, setShowModal] = useState(false);

 const perPage = 10;

 useEffect(() => {
 fetchApplications();
 }, []);

 const fetchApplications = async () => {
 try {
 const res = await api.get("/recruiter/applications");

 // Handle different response structures
 let applicationsData = [];

 if (Array.isArray(res.data)) {
 applicationsData = res.data;
 } else if (res.data && Array.isArray(res.data.data)) {
 applicationsData = res.data.data;
 } else if (res.data && Array.isArray(res.data.applications)) {
 applicationsData = res.data.applications;
 } else {
 console.error("Unexpected response structure:", res.data);
 toast.error("Could not parse applications data");
 return;
 }

 // Map applications with safe property access
 const mappedApps = applicationsData.map((app) => {
 // Extract applier name
 let applierName = "Unknown Applicant";
 let applierEmail = "No email";
 let jobTitle = "Unknown Job";

 if (app.fullName) {
 applierName = app.fullName;
 } else if (app.applier?.fullName) {
 applierName = app.applier.fullName;
 } else if (app.applier?.applierProfile?.fullName) {
 applierName = app.applier.applierProfile.fullName;
 } else if (app.applierName) {
 applierName = app.applierName;
 } else if (app.applier?.email) {
 applierName = app.applier.email.split("@")[0];
 }

 // Extract email
 if (app.applierEmail) {
 applierEmail = app.applierEmail;
 } else if (app.applier?.email) {
 applierEmail = app.applier.email;
 }

 // Extract job title
 if (app.jobTitle) {
 jobTitle = app.jobTitle;
 } else if (app.job?.title) {
 jobTitle = app.job.title;
 } else if (app.title) {
 jobTitle = app.title;
 }

 return {
 id: app.id,
 status: (app.status || "PENDING").toUpperCase(),
 appliedAt: app.appliedAt || app.createdAt || new Date().toISOString(),
 coverLetter: app.coverLetter || "",
 whyMe: app.whyMe || "",
 resumeUrl:
 app.resumeUrl ||
 app.applier?.resumeUrl ||
 app.applier?.applierProfile?.resumeUrl ||
 null,
 university:
 app.university || app.applier?.applierProfile?.university || null,
 gpa: app.gpa || app.applier?.applierProfile?.gpa || null,
 gender: app.gender || app.applier?.applierProfile?.gender || null,
 phone: app.phone || app.applier?.applierProfile?.phone || null,
 education:
 app.education || app.applier?.applierProfile?.education || null,
 applierName,
 applierEmail,
 jobTitle,
 };
 });

 setApplications(mappedApps);
 setFilteredApps(mappedApps);
 } catch (err) {
 console.error("Failed to load applications:", err);
 toast.error("Failed to load applications");
 } finally {
 setLoading(false);
 }
 };

 // Filter logic
 useEffect(() => {
 if (!Array.isArray(applications)) return;
 let filtered = applications;
 if (statusFilter !== "all") {
 filtered = filtered.filter(
 (a) => a.status === statusFilter.toUpperCase()
 );
 }
 if (search) {
 const q = search.toLowerCase();
 filtered = filtered.filter(
 (a) =>
 a.applierName.toLowerCase().includes(q) ||
 a.applierEmail.toLowerCase().includes(q) ||
 a.jobTitle.toLowerCase().includes(q)
 );
 }
 setFilteredApps(filtered);
 setCurrentPage(1);
 }, [search, statusFilter, applications]);

 // Pagination logic
 const totalPages = Math.ceil(filteredApps.length / perPage);
 const start = (currentPage - 1) * perPage;
 const paginated = filteredApps.slice(start, start + perPage);

 if (loading) {
 return <Loader label="Loading applications..." />;
 }

 return (
 <RecruiterPageShell
 title="Manage Applications"
 subtitle="Review and manage job applications from candidates"
 onBack={() => navigate("/recruiter/dashboard")}
 >
 {/* Filters */}
 <div className={`${glassPanel} mb-6 !p-4 sm:!p-5`}>
 <div className="flex flex-col sm:flex-row gap-4">
 {/* Search Input */}
 <div className="relative flex-1">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
 <Input
 placeholder="Search by applicant, email, or job title..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 className="pl-10 h-11 bg-card border-border focus:border-primary focus:ring-1 focus:ring-primary/20 transition-colors"
 />
 </div>
 {/* Status Filter Select */}
 <div className="w-full sm:w-48">
 <Select value={statusFilter} onValueChange={setStatusFilter}>
 <SelectTrigger className="h-11 bg-card border-border focus:ring-1 focus:ring-primary/20">
 <div className="flex items-center gap-2">
 <Filter className="w-4 h-4 text-muted-foreground" />
 <SelectValue placeholder="Filter by status" />
 </div>
 </SelectTrigger>
 <SelectContent className="bg-card border-border">
 <SelectItem value="all" className="focus:bg-accent">
 <div className="flex items-center gap-2">
 <div className="w-2 h-2 rounded-full bg-muted" />
 All Statuses
 </div>
 </SelectItem>
 <SelectItem value="PENDING" className="focus:bg-accent">
 <div className="flex items-center gap-2">
 <div className="w-2 h-2 rounded-full bg-yellow-500/100" />
 Pending
 </div>
 </SelectItem>
 <SelectItem value="ACCEPTED" className="focus:bg-accent">
 <div className="flex items-center gap-2">
 <div className="w-2 h-2 rounded-full bg-green-500/100" />
 Accepted
 </div>
 </SelectItem>
 <SelectItem value="REJECTED" className="focus:bg-accent">
 <div className="flex items-center gap-2">
 <div className="w-2 h-2 rounded-full bg-red-500" />
 Rejected
 </div>
 </SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>
 </div>

 {/* Stats */}
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
 <RecruiterStatCard
 title="Pending"
 value={applications.filter((a) => a.status === "PENDING").length}
 icon={Clock}
 accent="yellow"
 />
 <RecruiterStatCard
 title="Accepted"
 value={applications.filter((a) => a.status === "ACCEPTED").length}
 icon={CheckCircle}
 accent="green"
 />
 <RecruiterStatCard
 title="Rejected"
 value={applications.filter((a) => a.status === "REJECTED").length}
 icon={XCircle}
 accent="amber"
 />
 </div>

 {/* Table - Desktop View (hidden on mobile) */}
 <Card className={`rounded-2xl overflow-hidden hidden md:block ${glassCard}`}>
 <CardHeader className="bg-gradient-to-r from-card to-card/50 border-b border-border">
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
 <CardTitle className="text-lg font-semibold">
 Applications ({filteredApps.length})
 </CardTitle>
 <div className="text-sm text-muted-foreground">
 Page {currentPage} of {totalPages || 1}
 </div>
 </div>
 </CardHeader>
 <CardContent className="p-0">
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead className="">
 <tr>
 <th className="px-4 sm:px-6 py-4 text-left text-xs font-medium text-foreground uppercase tracking-wider">
 <div className="flex items-center gap-2">
 <User className="w-4 h-4" />
 Applicant
 </div>
 </th>
 <th className="px-4 sm:px-6 py-4 text-left text-xs font-medium text-foreground uppercase tracking-wider">
 <div className="flex items-center gap-2">
 <Briefcase className="w-4 h-4" />
 Job Title
 </div>
 </th>
 <th className="px-4 sm:px-6 py-4 text-left text-xs font-medium text-foreground uppercase tracking-wider hidden lg:table-cell">
 <div className="flex items-center gap-2">
 <Mail className="w-4 h-4" />
 Email
 </div>
 </th>
 <th className="px-4 sm:px-6 py-4 text-left text-xs font-medium text-foreground uppercase tracking-wider hidden xl:table-cell">
 <div className="flex items-center gap-2">
 <Calendar className="w-4 h-4" />
 Date
 </div>
 </th>
 <th className="px-4 sm:px-6 py-4 text-center text-xs font-medium text-foreground uppercase tracking-wider">
 Resume
 </th>
 <th className="px-4 sm:px-6 py-4 text-center text-xs font-medium text-foreground uppercase tracking-wider">
 Status
 </th>
 <th className="px-4 sm:px-6 py-4 text-center text-xs font-medium text-foreground uppercase tracking-wider">
 Actions
 </th>
 </tr>
 </thead>
 <tbody className="divide-y divide-border">
 {paginated.length === 0 ? (
 <tr>
 <td colSpan="7" className="px-6 py-12 text-center">
 <div className="flex flex-col items-center gap-3">
 <div className="p-4 rounded-full ">
 <FileText className="w-8 h-8 text-muted-foreground" />
 </div>
 <div>
 <p className="text-foreground font-medium">
 No applications found
 </p>
 <p className="text-sm text-muted-foreground mt-1">
 {search || statusFilter !== "all"
 ? "Try adjusting your filters"
 : "You haven't received any applications yet"}
 </p>
 </div>
 </div>
 </td>
 </tr>
 ) : (
 paginated.map((app) => (
 <tr
 key={app.id}
 className="hover: transition-colors"
 >
 {/* Applicant Column */}
 <td className="px-4 sm:px-6 py-4">
 <div className="flex items-center gap-3">
 <div className="w-9 h-9 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 flex items-center justify-center shrink-0">
 <span className="text-sm font-semibold text-primary">
 {app.applierName.charAt(0).toUpperCase()}
 </span>
 </div>
 <div className="min-w-0">
 <p className="font-medium text-foreground truncate">
 {app.applierName}
 </p>
 </div>
 </div>
 </td>

 {/* Job Title Column */}
 <td className="px-4 sm:px-6 py-4">
 <span className="text-sm font-medium text-foreground">
 {app.jobTitle}
 </span>
 </td>

 {/* Email Column - Hidden on mobile/tablet */}
 <td className="px-4 sm:px-6 py-4 hidden lg:table-cell text-sm text-muted-foreground">
 {app.applierEmail}
 </td>

 {/* Date Column - Hidden on mobile/tablet/desktop, shown on XL */}
 <td className="px-4 sm:px-6 py-4 hidden xl:table-cell">
 <span className="text-xs text-muted-foreground">
 {new Date(app.appliedAt).toLocaleDateString()}
 </span>
 </td>

 {/* Resume Column */}
 <td className="px-4 sm:px-6 py-4 text-center">
 {app.resumeUrl ? (
 <Button
 variant="ghost"
 size="sm"
 className="h-8 w-8 p-0 hover:text-primary hover:bg-primary/10"
 onClick={(e) => {
 e.stopPropagation();
 const url = app.resumeUrl.startsWith("http")
 ? app.resumeUrl
 : `${process.env.REACT_APP_API_URL || "http://localhost:5000"}${app.resumeUrl}`;
 window.open(url, "_blank");
 }}
 title="Download Resume"
 >
 <Download className="w-4 h-4" />
 </Button>
 ) : (
 <span className="text-[10px] text-muted-foreground italic">
 N/A
 </span>
 )}
 </td>

 {/* Status Column */}
 <td className="px-4 sm:px-6 py-4 text-center">
 <Badge
 variant={
 app.status === "ACCEPTED"
 ? "default"
 : app.status === "REJECTED"
 ? "destructive"
 : "secondary"
 }
 className="capitalize flex items-center gap-1 text-[10px] justify-center"
 >
 {app.status === "PENDING" && (
 <Clock className="w-2.5 h-2.5" />
 )}
 {app.status === "ACCEPTED" && (
 <CheckCircle className="w-2.5 h-2.5" />
 )}
 {app.status === "REJECTED" && (
 <XCircle className="w-2.5 h-2.5" />
 )}
 <span>{app.status.toLowerCase()}</span>
 </Badge>
 </td>

 {/* Actions Column */}
 <td className="px-4 sm:px-6 py-4 text-center">
 <Button
 variant="outline"
 size="sm"
 className="h-8 px-3 text-xs border-border hover:bg-accent"
 onClick={() => {
 setSelectedApp(app);
 setShowModal(true);
 }}
 >
 <Eye className="w-3.5 h-3.5 mr-1" />
 <span>Details</span>
 </Button>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>
 </CardContent>
 </Card>

 {/* Card View - Mobile Only */}
 <div className="md:hidden space-y-4">
 {paginated.length === 0 ? (
 <RecruiterEmptyState
 icon={FileText}
 title="No applications found"
 description={
 search || statusFilter !== "all"
 ? "Try adjusting your filters"
 : "You haven't received any applications yet"
 }
 />
 ) : (
 paginated.map((app) => (
 <Card
 key={app.id}
 className={`rounded-2xl ${glassCardHover}`}
 >
 <CardContent className="p-4">
 <div className="flex items-start gap-3 mb-3">
 <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 flex items-center justify-center shrink-0">
 <span className="text-sm font-semibold text-primary">
 {app.applierName.charAt(0).toUpperCase()}
 </span>
 </div>
 <div className="flex-1 min-w-0">
 <h3 className="font-semibold text-base text-foreground truncate">
 {app.applierName}
 </h3>
 <p className="text-xs text-muted-foreground truncate">
 {app.applierEmail}
 </p>
 </div>
 <Badge
 variant={
 app.status === "ACCEPTED"
 ? "default"
 : app.status === "REJECTED"
 ? "destructive"
 : "secondary"
 }
 className="text-xs shrink-0"
 >
 {app.status === "PENDING" && (
 <Clock className="w-3 h-3 mr-1" />
 )}
 {app.status === "ACCEPTED" && (
 <CheckCircle className="w-3 h-3 mr-1" />
 )}
 {app.status === "REJECTED" && (
 <XCircle className="w-3 h-3 mr-1" />
 )}
 {app.status.toLowerCase()}
 </Badge>
 </div>

 <div className="space-y-2 mb-3">
 <div className="flex items-center gap-2 text-xs text-muted-foreground">
 <Briefcase className="w-3.5 h-3.5 shrink-0" />
 <span className="truncate">{app.jobTitle}</span>
 </div>
 <div className="flex items-center gap-2 text-xs text-muted-foreground">
 <Calendar className="w-3.5 h-3.5 shrink-0" />
 <span>
 {new Date(app.appliedAt).toLocaleDateString()}
 </span>
 </div>
 </div>

 <div className="flex gap-2">
 {app.resumeUrl && (
 <Button
 variant="outline"
 size="sm"
 className="flex-1"
 onClick={(e) => {
 e.stopPropagation();
 const url = app.resumeUrl.startsWith("http")
 ? app.resumeUrl
 : `${process.env.REACT_APP_API_URL || "http://localhost:5000"}${app.resumeUrl}`;
 window.open(url, "_blank");
 }}
 >
 <Download className="w-3.5 h-3.5 mr-1" />
 Resume
 </Button>
 )}
 <Button
 variant="default"
 size="sm"
 className="flex-1"
 onClick={() => {
 setSelectedApp(app);
 setShowModal(true);
 }}
 >
 <Eye className="w-3.5 h-3.5 mr-1" />
 Details
 </Button>
 </div>
 </CardContent>
 </Card>
 ))
 )}
 </div>

 {/* Pagination */}
 <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-8">
 <div className="text-sm text-muted-foreground text-center sm:text-left">
 Showing {Math.min(start + 1, filteredApps.length)}–
 {Math.min(start + perPage, filteredApps.length)} of{" "}
 {filteredApps.length} applications
 </div>
 <div className="flex items-center gap-2">
 <Button
 variant="outline"
 size="sm"
 disabled={currentPage === 1}
 onClick={() => setCurrentPage((p) => p - 1)}
 className="h-9 w-9 p-0 border-border hover:bg-accent disabled:opacity-50"
 >
 <ChevronLeft className="w-4 h-4" />
 </Button>
 <div className="flex items-center gap-1">
 {totalPages > 0 &&
 Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
 let pageNum;
 if (totalPages <= 5) {
 pageNum = i + 1;
 } else if (currentPage <= 3) {
 pageNum = i + 1;
 } else if (currentPage >= totalPages - 2) {
 pageNum = totalPages - 4 + i;
 } else {
 pageNum = currentPage - 2 + i;
 }

 return (
 <Button
 key={pageNum}
 variant={currentPage === pageNum ? "default" : "outline"}
 size="sm"
 onClick={() => setCurrentPage(pageNum)}
 className="h-9 w-9 border-border hover:bg-accent"
 >
 {pageNum}
 </Button>
 );
 })}
 </div>
 <Button
 variant="outline"
 size="sm"
 disabled={currentPage === totalPages || totalPages === 0}
 onClick={() => setCurrentPage((p) => p + 1)}
 className="h-9 w-9 p-0 border-border hover:bg-accent disabled:opacity-50"
 >
 <ChevronRight className="w-4 h-4" />
 </Button>
 </div>
 </div>

 {/* Details Modal */}
 <Dialog open={showModal} onOpenChange={setShowModal}>
 <DialogContent className="w-[95vw] sm:max-w-xl max-w-[95vw] max-h-[90vh] overflow-y-auto bg-card border-border p-4 sm:p-6">
 <DialogHeader>
 <DialogTitle className="text-base sm:text-lg text-foreground">
 Application Details
 </DialogTitle>
 <DialogDescription className="text-xs sm:text-sm">
 Review applicant information and cover letter
 </DialogDescription>
 </DialogHeader>

 {selectedApp && (
 <div className="space-y-4 sm:space-y-6 py-2 sm:py-4">
 {/* Applicant Info */}
 <div className="grid grid-cols-1 gap-3 sm:gap-4">
 <div className="p-3 sm:p-4 rounded-lg bg-gradient-to-br from-card to-card/50 border border-border">
 <label className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase block mb-2">
 Applicant Info
 </label>
 <p className="font-semibold text-base sm:text-lg mb-1 text-foreground break-words">
 {selectedApp.applierName}
 </p>
 <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
 <Mail className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
 <span className="truncate">
 {selectedApp.applierEmail}
 </span>
 </div>
 </div>

 <div className="p-3 sm:p-4 rounded-lg bg-gradient-to-br from-card to-card/50 border border-border">
 <label className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase block mb-2">
 Job Details
 </label>
 <p className="font-semibold text-base sm:text-lg mb-1 text-foreground break-words">
 {selectedApp.jobTitle}
 </p>
 <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
 <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
 <span>
 Applied on{" "}
 {new Date(selectedApp.appliedAt).toLocaleDateString()}
 </span>
 </div>
 </div>
 </div>

 {/* Cover Letter */}
 {selectedApp.coverLetter && (
 <div className="p-3 sm:p-4 rounded-lg bg-gradient-to-br from-muted/10 to-muted/5 border border-border">
 <label className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase block mb-2 sm:mb-3 flex items-center gap-2">
 <FileText className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
 Cover Letter
 </label>
 <div className="text-xs sm:text-sm text-foreground whitespace-pre-wrap leading-relaxed p-2 sm:p-3 rounded /50 border border-border/50 max-h-40 sm:max-h-60 overflow-y-auto">
 {selectedApp.coverLetter}
 </div>
 </div>
 )}

 {/* Why Me */}
 {selectedApp.whyMe && (
 <div className="p-3 sm:p-4 rounded-lg bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
 <label className="text-[10px] sm:text-xs font-medium text-primary uppercase block mb-2 sm:mb-3 flex items-center gap-2">
 <Zap className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
 Why Me?
 </label>
 <div className="text-xs sm:text-sm text-foreground whitespace-pre-wrap leading-relaxed p-2 sm:p-3 rounded /50 border border-border/50 max-h-40 sm:max-h-60 overflow-y-auto">
 {selectedApp.whyMe}
 </div>
 </div>
 )}

 {/* Resume & Status */}
 <div className="flex flex-col gap-3 pt-3 sm:pt-4 border-t border-border">
 {selectedApp.resumeUrl ? (
 <Button
 variant="outline"
 onClick={() => {
 const url = selectedApp.resumeUrl.startsWith("http")
 ? selectedApp.resumeUrl
 : `${process.env.REACT_APP_API_URL || "http://localhost:5000"}${selectedApp.resumeUrl}`;
 window.open(url, "_blank");
 }}
 className="flex items-center justify-center gap-2 w-full h-10 sm:h-11 border-border hover:bg-accent"
 >
 <Download className="w-4 h-4" />
 <span className="text-sm">Download Resume</span>
 </Button>
 ) : (
 <div className="text-center p-3 rounded-lg">
 <span className="text-xs sm:text-sm text-muted-foreground italic">
 No resume provided
 </span>
 </div>
 )}

 <div className="flex items-center justify-center gap-2 sm:gap-3 p-3 rounded-lg">
 <span className="text-xs sm:text-sm font-medium text-foreground">
 Status:
 </span>
 <Badge
 variant={
 selectedApp.status === "ACCEPTED"
 ? "default"
 : selectedApp.status === "REJECTED"
 ? "destructive"
 : "secondary"
 }
 className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-1.5"
 >
 {selectedApp.status === "PENDING" && (
 <Clock className="w-3 h-3 mr-1" />
 )}
 {selectedApp.status === "ACCEPTED" && (
 <CheckCircle className="w-3 h-3 mr-1" />
 )}
 {selectedApp.status === "REJECTED" && (
 <XCircle className="w-3 h-3 mr-1" />
 )}
 {selectedApp.status}
 </Badge>
 </div>
 </div>
 </div>
 )}
 </DialogContent>
 </Dialog>
 </RecruiterPageShell>
 );
}
